// AX실 담당자 고르기 — ② 검토 카드 안(AX · ② 고치기 중이 아닐 때만 · 그리는 쪽은 DevRequestDetail).
//   후보 = 서버 검토자 명단(GET /assignee-candidates · 기준은 서버 _assignee_candidates 하나) · 첫 항목 «미지정» = 해제.
//   고르면 바로 저장(setAssignee) → onChanged · 실패면 서버 detail 을 그 자리에 빨간 글씨(고른 값은 되돌린다) · 저장 중 비활성.
//   지금 담당자가 후보에 없으면(명단에서 빠짐 등) «<이름> (지정 불가)»로 남겨 값이 사라져 보이지 않게 한다.
// 🔴 고르는 상자만 그린다 — «AX 담당자» 줄(이름)은 ② 카드의 칸 목록이 보인다(요청자에게도).
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, input as inputStyle } from '../../utils/uiStyles';
import { errorText } from './devreqLabels';

export const UNASSIGNED_LABEL = '미지정';
export const NOT_ASSIGNABLE = '지정 불가';
const LOAD_FAILED = '담당자 후보를 불러오지 못했습니다.';
const SAVE_FAILED = '담당자를 저장하지 못했습니다.';

const idText = (value) => (value === null || value === undefined ? '' : String(value));

// 후보 이름 — 같은 이름이 둘 이상이면 이메일을 덧붙인다.
function candidateLabels(candidates) {
  const counts = {};
  candidates.forEach((c) => { counts[c.name || ''] = (counts[c.name || ''] || 0) + 1; });
  return candidates.map((c) => {
    const name = c.name || c.email || idText(c.user_id);
    return counts[c.name || ''] > 1 && c.email ? `${name} (${c.email})` : name;
  });
}

export default function DevRequestAssignee({ item, onChanged }) {
  const current = idText(item?.assignee_id);
  const [candidates, setCandidates] = useState(null);   // null = 불러오는 중
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 상세를 다시 읽으면 서버 값으로 맞춘다.
  useEffect(() => { setValue(current); }, [current]);

  useEffect(() => {
    let alive = true;
    // 부르는 중 던져도(값이 없어도) 사슬 안이라 catch·빈 목록으로 간다.
    Promise.resolve()
      .then(() => devreqAPI.assigneeCandidates())
      .then((r) => { if (alive) setCandidates(Array.isArray(r?.data) ? r.data : []); })
      .catch(async (err) => {
        const message = await errorText(err, LOAD_FAILED);
        if (!alive) return;
        setCandidates([]);
        setError(message);
      });
    return () => { alive = false; };
  }, []);

  const choose = async (event) => {
    const next = event.target.value;
    if (busyRef.current || !item || next === value) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setValue(next);
    try {
      await devreqAPI.setAssignee(item.id, next === '' ? null : Number(next));
      if (aliveRef.current) onChanged?.();
    } catch (err) {
      const message = await errorText(err, SAVE_FAILED);
      if (aliveRef.current) {
        setValue(current);
        setError(message);
      }
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const list = candidates || [];
  const labels = candidateLabels(list);
  // 지금 담당자가 후보 목록에 없으면 그 이름을 따로 남긴다(불러오는 동안은 이름만 · 다 불러온 뒤엔 «지정 불가»).
  const outside = current !== '' && !list.some((c) => idText(c.user_id) === current);
  const currentName = item?.assignee_name || current;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', padding: '0 14px 12px' }}>
      <span style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textSub }}>AX 담당자 지정</span>
      <select aria-label="AX 담당자" value={value} onChange={choose} disabled={busy || candidates === null}
        style={{ ...inputStyle, width: 'auto', minWidth: '140px', flex: '1 1 160px', padding: '6px 10px', fontSize: '13px' }}>
        <option value="">{UNASSIGNED_LABEL}</option>
        {outside && (
          <option value={current} disabled={candidates !== null}>
            {candidates === null ? currentName : `${currentName} (${NOT_ASSIGNABLE})`}
          </option>
        )}
        {list.map((c, i) => <option key={c.user_id} value={idText(c.user_id)}>{labels[i]}</option>)}
      </select>
      {error && <div role="alert" style={{ flexBasis: '100%', fontSize: '12px', color: COLORS.dangerText }}>{error}</div>}
    </div>
  );
}
